import { Layout, Menu, Dropdown, Avatar, Alert, Badge } from "antd";
import { Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";
import { usePermissions } from "../../../contexts/PermissionContext";
import Logo from "./../../../assets/logo.svg";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  AuditOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  LogoutOutlined,
  SecurityScanOutlined,
  StarOutlined,
  UserOutlined,
  LockOutlined,
  DollarOutlined,
  TeamOutlined,
  BankOutlined,
  CommentOutlined,
  SettingOutlined,
  FileTextOutlined,
  LineChartOutlined,
  UsergroupAddOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  ProfileOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { PartnerDashboardWrapper } from "./PartnerDashboard.style";
import NotificationBell from "../../../components/NotificationBell/NotificationBell";
import PartnerOverview from "../PartnerOverview/PartnerOverview";
import OngoingJobs from "../OngoingJobs/OngoingJobs";
import TalentsHired from "../TalentsHired/TalentsHired";
import SubmitProfiles from "../SubmitProfiles/SubmitProfiles";
import BenchPool from "../BenchPool/BenchPool";
import TalentDetails from "../TalentDetails/TalentDetails";
import AddTalentProfile from "../AddTalentProfile/AddTalentProfile";
import Account from "../../../components/Account/Account";
import PartnerTimesheet from "../Timesheet/Timesheet";
import PartnerFinance from "../Finance/Finance";
import OrganizationProfile from "../../Admin/Organization/OrganizationProfile";
import CompanyDetails from "../../Admin/Organization/CompanyDetails";
import IssuesFeedback from "../../Customer/IssuesFeedback/IssuesFeedback";
import ProfileRoles from "../../Shared/ProfileRoles/ProfileRoles";
import TeamMembers from "../../Shared/TeamMembers/TeamMembers";
import FinancialInsights from "../FinancialInsights/FinancialInsights";
import MyClients from "../MyClients/MyClients";
import PartnerClientDetail from "../MyClients/PartnerClientDetail";
import HelpCenter from "../HelpCenter/HelpCenter";
import Referrals from "../Referrals/Referrals";
import TalentPerformance from "../TalentPerformance/TalentPerformance";
import PartnerTalentPerformanceDetail from "../TalentPerformance/PartnerTalentPerformanceDetail";
import InterviewRequests from "../InterviewRequests/InterviewRequests";
import PartnerSubmittedProfiles from "../SubmittedProfiles/PartnerSubmittedProfiles";
import ComplianceDocuments from "../ComplianceDocuments/ComplianceDocuments";
import { AdminJobFullDetails } from "../../Admin/JobRequirments/JobDetails";

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessMenu, loading: permissionsLoading, permissionsLoaded, refreshPermissions } = usePermissions();
  const [openKeys, setOpenKeys] = useState([]);
  const [pendingComplianceCount, setPendingComplianceCount] = useState(0);

  const fetchPendingComplianceCount = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const res = await axios.post(
        API_CONST.COMPLIANCE_DOCUMENTS_LIST,
        { status: "pending" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const docs = res.data.documents || [];
      const uniqueHiringIds = new Set(docs.map((d) => d.hiringRecordId));
      setPendingComplianceCount(uniqueHiringIds.size);
    } catch {
      // silent — badge is non-critical
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    
    if (!token || userRole !== "partner") {
      navigate("/", { replace: true });
    } else {
      refreshPermissions();
      fetchPendingComplianceCount();
    }
  }, [navigate, refreshPermissions, fetchPendingComplianceCount]);

  // Re-fetch the sidebar badge count whenever a document is signed in the child page,
  // so the badge stays accurate without requiring a full page reload
  useEffect(() => {
    const handleComplianceSigned = () => fetchPendingComplianceCount();
    window.addEventListener('complianceSigned', handleComplianceSigned);
    return () => window.removeEventListener('complianceSigned', handleComplianceSigned);
  }, [fetchPendingComplianceCount]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/partner/profile-roles") || path.includes("/partner/team-members")) {
      setOpenKeys(["users-roles"]);
    } else if (path.includes("/partner/ongoing-jobs") || path.includes("/partner/interview-requests") ||
               path.includes("/partner/submitted-profiles")) {
      setOpenKeys(["requirements"]);
    } else if (path.includes("/partner/bench-pool") || path.includes("/partner/talent-hired") ||
               path.includes("/partner/talent-details") || path.includes("/partner/add-talent") ||
               path.includes("/partner/compliance-docs") || path.includes("/partner/talent-performance")) {
      setOpenKeys(["talents"]);
    } else if (path.includes("/partner/timesheet") || path.includes("/partner/invoice") || 
               path.includes("/partner/financial-insights") || path.includes("/partner/finance")) {
      setOpenKeys(["billing-finance"]);
    }
  }, [location.pathname]);

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const sideBarMenu = useMemo(() => {
    const items = [
      { 
        key: "/partner", 
        label: "Dashboard", 
        icon: <DashboardOutlined /> 
      },
      {
        key: "/partner/my-clients",
        label: "My Clients",
        icon: <BankOutlined />,
      },
      {
        key: "users-roles",
        label: "Users & Roles",
        icon: <TeamOutlined />,
        children: [
          {
            key: "/partner/profile-roles",
            label: "Role Management",
            icon: <SettingOutlined />,
          },
          {
            key: "/partner/team-members",
            label: "Team Members",
            icon: <UsergroupAddOutlined />,
          },
        ],
      },
      {
        key: "requirements",
        label: "Requirements",
        icon: <SecurityScanOutlined />,
        children: [
          {
            key: "/partner/ongoing-jobs",
            label: "Ongoing Jobs",
            icon: <SecurityScanOutlined />,
          },
          {
            key: "/partner/interview-requests",
            label: "Interview Requests",
            icon: <CalendarOutlined />,
          },
          {
            key: "/partner/submitted-profiles",
            label: "Submitted Profiles",
            icon: <ProfileOutlined />,
          },
        ],
      },
      {
        key: "talents",
        label: "Talents",
        icon: <UserOutlined />,
        children: [
          {
            key: "/partner/bench-pool",
            label: "Bench Pool",
            icon: <UserOutlined />,
          },
          {
            key: "/partner/talent-hired",
            label: "Talents Hired",
            icon: <AuditOutlined />,
          },
          {
            key: "/partner/compliance-docs",
            label: pendingComplianceCount > 0 ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Compliance Docs
                <Badge
                  count={pendingComplianceCount}
                  size="small"
                  style={{ background: "#ff4d4f" }}
                />
              </span>
            ) : "Compliance Docs",
            icon: <FileTextOutlined />,
          },
          {
            key: "/partner/talent-performance",
            label: "Performance",
            icon: <StarOutlined />,
          },
        ],
      },
      {
        key: "billing-finance",
        label: "Billing & Finance",
        icon: <DollarOutlined />,
        children: [
          {
            key: "/partner/timesheet",
            label: "Timesheet",
            icon: <FieldTimeOutlined />,
          },
          {
            key: "/partner/invoice",
            label: "Invoice",
            icon: <FileTextOutlined />,
          },
          {
            key: "/partner/financial-insights",
            label: "Financial Insights",
            icon: <LineChartOutlined />,
          },
        ],
      },
      {
        key: "/partner/referrals",
        label: "Referrals",
        icon: <GiftOutlined />,
      },
      {
        key: "/partner/issues-feedback",
        label: "Issues & Feedback",
        icon: <CommentOutlined />,
      },
      {
        key: "/partner/help-center",
        label: "Help Center",
        icon: <QuestionCircleOutlined />,
      },
    ];

    return items;
  }, [pendingComplianceCount]);

  const filteredSideBarMenu = useMemo(() => {
    if (permissionsLoading || !permissionsLoaded) return sideBarMenu;
    
    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        if (item.children) {
          const filteredChildren = item.children.filter(child => canAccessMenu(child.key));
          if (filteredChildren.length > 0) {
            acc.push({ ...item, children: filteredChildren });
          }
        } else if (canAccessMenu(item.key)) {
          acc.push(item);
        }
        return acc;
      }, []);
    };
    
    return filterItems(sideBarMenu);
  }, [canAccessMenu, permissionsLoading, permissionsLoaded, sideBarMenu]);

  const sideBarMenu2 = [
    { key: "/logout", label: "Logout", icon: <LogoutOutlined /> },
  ];

  const handleClick = (e) => {
    if (e.key === "/logout") {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userName");
      navigate("/", { replace: true });
    } else if (!e.key.startsWith("users-roles") && !e.key.startsWith("requirements") && 
               !e.key.startsWith("talents") && !e.key.startsWith("billing-finance")) {
      navigate(e.key);
    }
  };

  const profileMenuItems = [
    {
      key: 'account',
      label: 'View Account',
      icon: <UserOutlined />,
    },
    {
      key: 'company',
      label: 'View Company',
      icon: <BankOutlined />,
    },
    {
      key: 'change-password',
      label: 'Change Password',
      icon: <LockOutlined />,
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
    },
  ];

  const handleProfileMenuClick = ({ key }) => {
    if (key === 'logout') {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userName");
      navigate("/", { replace: true });
    } else if (key === 'account') {
      navigate("/partner/account");
    } else if (key === 'company') {
      navigate("/partner/company-details");
    } else if (key === 'change-password') {
      navigate("/partner/account");
    }
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === "/partner" || path === "/partner/") return "/partner";
    if (path.match(/^\/partner\/my-clients\/[^/]+/)) return "/partner/my-clients";
    if (path.startsWith("/partner/my-clients")) return "/partner/my-clients";
    if (path.startsWith("/partner/profile-roles")) return "/partner/profile-roles";
    if (path.startsWith("/partner/team-members")) return "/partner/team-members";
    if (path.startsWith("/partner/ongoing-jobs")) return "/partner/ongoing-jobs";
    if (path.startsWith("/partner/interview-requests")) return "/partner/interview-requests";
    if (path.startsWith("/partner/submitted-profiles")) return "/partner/submitted-profiles";
    if (path.startsWith("/partner/bench-pool")) return "/partner/bench-pool";
    if (path.startsWith("/partner/add-talent")) return "/partner/bench-pool";
    if (path.startsWith("/partner/submit-profiles")) return "/partner/ongoing-jobs";
    if (path.startsWith("/partner/talent-hired")) return "/partner/talent-hired";
    if (path.startsWith("/partner/talent-details")) return "/partner/talent-hired";
    if (path.startsWith("/partner/compliance-docs")) return "/partner/compliance-docs";
    if (path.startsWith("/partner/talent-performance")) return "/partner/talent-performance";
    if (path.startsWith("/partner/timesheet")) return "/partner/timesheet";
    if (path.startsWith("/partner/invoice")) return "/partner/invoice";
    if (path.startsWith("/partner/finance")) return "/partner/invoice";
    if (path.startsWith("/partner/financial-insights")) return "/partner/financial-insights";
    if (path.startsWith("/partner/issues-feedback")) return "/partner/issues-feedback";
    if (path.startsWith("/partner/help-center")) return "/partner/help-center";
    if (path.startsWith("/partner/referrals")) return "/partner/referrals";
    return "/partner";
  };

  return (
    <PartnerDashboardWrapper>
      <Layout>
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            background: "#191919",
            padding: "0 24px",
            justifyContent: "space-between",
          }}
        >
          <img src={Logo} alt="logo" style={{ height: '35px', width: 'auto' }} />
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <NotificationBell />
            <Dropdown 
              menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }}
              placement="bottomRight"
            >
              <Avatar
                style={{
                  backgroundColor: "#00d9a9",
                  cursor: "pointer",
                }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </div>
        </Header>
        {localStorage.getItem("userIsActive") === "false" && (
          <Alert
            message="Your account is pending approval. You can view information but cannot create or edit until an admin approves your account."
            type="warning"
            showIcon
            banner
            style={{ borderRadius: 0 }}
          />
        )}
        <Layout>
          <Sider
            width={220}
            style={{
              background: "#272727",
            }}
            className="side-navbar"
          >
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              style={{
                borderRight: 0,
              }}
              items={filteredSideBarMenu}
              onClick={handleClick}
            />

            <Menu
              mode="inline"
              style={{
                borderRight: 0,
              }}
              items={sideBarMenu2}
              onClick={handleClick}
            />
          </Sider>
          <Layout
            style={{
              padding: "30px 30px 10px 30px",
              overflow: "auto",
              height: "90vh",
            }}
          >
            <Routes>
              <Route path="ongoing-jobs" element={<OngoingJobs />} />
              <Route path="interview-requests" element={<InterviewRequests />} />
              <Route path="submitted-profiles" element={<PartnerSubmittedProfiles />} />
              <Route path="bench-pool" element={<BenchPool />} />
              <Route path="add-talent" element={<AddTalentProfile />} />
              <Route path="submit-profiles/:jobId" element={<SubmitProfiles />} />
              <Route path="job-full-details/:jobId" element={<AdminJobFullDetails />} />
              <Route path="talent-hired" element={<TalentsHired />} />
              <Route path="compliance-docs" element={<ComplianceDocuments />} />
              <Route path="talent-performance" element={<TalentPerformance />} />
              <Route path="talent-performance/:talentId" element={<PartnerTalentPerformanceDetail />} />
              <Route path="talent-details/:talentId" element={<TalentDetails />} />
              <Route path="timesheet" element={<PartnerTimesheet />} />
              <Route path="invoice" element={<PartnerFinance />} />
              <Route path="finance" element={<Navigate to="/partner/invoice" replace />} />
              <Route path="financial-insights" element={<FinancialInsights />} />
              <Route path="issues-feedback" element={<IssuesFeedback />} />
              <Route path="my-clients" element={<MyClients />} />
              <Route path="my-clients/:relationId" element={<PartnerClientDetail />} />
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="profile-roles" element={<ProfileRoles parentRole="Partner" />} />
              <Route path="team-members" element={<TeamMembers role="partner" />} />
              <Route path="account" element={<Account role="partner" />} />
              <Route path="organization/:id" element={<OrganizationProfile />} />
              <Route path="company-details" element={<CompanyDetails />} />
              <Route path="*" element={<PartnerOverview />} />
            </Routes>
          </Layout>
        </Layout>
      </Layout>
    </PartnerDashboardWrapper>
  );
};

export default PartnerDashboard;
